import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Bed, Bath, Car, ChevronDown, MapPin, Building2 } from 'lucide-react';
import { clsx } from 'clsx';
import { supabase } from '../../lib/supabase';
import { servicesHaveAllAmenities } from '../../lib/amenity-slug';
import { MultiSelectDropdown } from './MultiSelectDropdown';

export interface ExchangeFilters {
  operation: 'All' | 'sale' | 'rent';
  propertyTypeIds: number[];
  departmentId: number | null;
  cityIds: number[];           // "Sector" in UI (cities table, legacy misnamed)
  sectorIds: number[];         // "Sub-sector" (sectors table)
  minPrice: string;
  maxPrice: string;
  bedrooms: number[];
  bathrooms: number[];
  parking: number[];
  buildMinM2: string;
  buildMaxM2: string;
  landMinM2: string;
  landMaxM2: string;
  amenityIds: number[];
}

export const defaultExchangeFilters: ExchangeFilters = {
  operation: 'All',
  propertyTypeIds: [],
  departmentId: null,
  cityIds: [],
  sectorIds: [],
  minPrice: '',
  maxPrice: '',
  bedrooms: [],
  bathrooms: [],
  parking: [],
  buildMinM2: '',
  buildMaxM2: '',
  landMinM2: '',
  landMaxM2: '',
  amenityIds: [],
};

export function countActiveExchangeFilters(f: ExchangeFilters): number {
  let n = 0;
  if (f.operation !== 'All') n++;
  if (f.propertyTypeIds.length) n++;
  if (f.departmentId != null) n++;
  if (f.cityIds.length) n++;
  if (f.sectorIds.length) n++;
  if (f.minPrice || f.maxPrice) n++;
  if (f.bedrooms.length) n++;
  if (f.bathrooms.length) n++;
  if (f.parking.length) n++;
  if (f.buildMinM2 || f.buildMaxM2) n++;
  if (f.landMinM2 || f.landMaxM2) n++;
  if (f.amenityIds.length) n++;
  return n;
}

export function applyExchangeFilters<T extends Record<string, any>>(
  list: T[],
  f: ExchangeFilters,
  // id → canonical amenity name, from the `amenities` catalog. Needed because
  // the filter stores amenity ids but the data to match lives in the property's
  // `services` array (names/slugs). Pass it so selected ids can be resolved to
  // names for the containment check; when absent/not-yet-loaded the amenity
  // constraint is skipped (avoids hiding everything on first paint).
  amenityNameById?: Map<number, string>,
): T[] {
  const minPrice = f.minPrice ? Number(f.minPrice) : null;
  const maxPrice = f.maxPrice ? Number(f.maxPrice) : null;
  const buildMin = f.buildMinM2 ? Number(f.buildMinM2) : null;
  const buildMax = f.buildMaxM2 ? Number(f.buildMaxM2) : null;
  const landMin = f.landMinM2 ? Number(f.landMinM2) : null;
  const landMax = f.landMaxM2 ? Number(f.landMaxM2) : null;

  return list.filter((p) => {
    if (f.operation !== 'All' && p.operation !== f.operation) return false;
    if (f.propertyTypeIds.length && !f.propertyTypeIds.includes(Number(p.property_type_id))) return false;
    if (f.departmentId != null && Number(p.department_id) !== f.departmentId) return false;
    if (f.cityIds.length && !f.cityIds.includes(Number(p.city_id))) return false;
    if (f.sectorIds.length && !f.sectorIds.includes(Number(p.sector_id))) return false;

    const price = Number(p.price ?? 0);
    if (minPrice != null && price < minPrice) return false;
    if (maxPrice != null && price > maxPrice) return false;

    if (f.bedrooms.length) {
      const b = Number(p.bedrooms ?? 0);
      const matches = f.bedrooms.some((n) => (n === 5 ? b >= 5 : b === n));
      if (!matches) return false;
    }
    if (f.bathrooms.length) {
      const b = Number(p.bathrooms ?? 0);
      const matches = f.bathrooms.some((n) => (n === 4 ? b >= 4 : b === n));
      if (!matches) return false;
    }
    if (f.parking.length) {
      const b = Number(p.parking ?? 0);
      const matches = f.parking.some((n) => (n === 3 ? b >= 3 : b === n));
      if (!matches) return false;
    }

    const built = Number(p.built_up_area ?? 0);
    if (buildMin != null && built < buildMin) return false;
    if (buildMax != null && built > buildMax) return false;

    const land = Number(p.lot_size_varas ?? 0);
    if (landMin != null && land < landMin) return false;
    if (landMax != null && land > landMax) return false;

    if (f.amenityIds.length) {
      // Amenities are stored in properties.services (JSONB array), mixing canonical
      // names ("Balcón", new CRM saves) with legacy slug keys ("balcon", migrated
      // rows) — the property_amenities junction is empty for shared listings. Resolve
      // the selected ids to catalog names, then match either form against services,
      // mirroring the main PropiedadesView filter. Skip the constraint until the
      // catalog is loaded so the list isn't emptied on first paint.
      const names = amenityNameById
        ? f.amenityIds.map((id) => amenityNameById.get(id)).filter(Boolean) as string[]
        : [];
      if (names.length && !servicesHaveAllAmenities(p.services, names)) return false;
    }

    return true;
  });
}

const formatWithCommas = (value: string | number | undefined) => {
  if (value === undefined || value === '') return '';
  const stringValue = value.toString().replace(/,/g, '');
  const parts = stringValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const parseCommas = (v: string) => v.replace(/,/g, '');

interface Props {
  isOpen: boolean;
  onClose: () => void;
  theme: 'light' | 'dark' | 'high-contrast';
  currentFilters: ExchangeFilters;
  onApplyFilters: (filters: ExchangeFilters) => void;
  properties: any[]; // sharedProperties — used to derive available options
}

export const ExchangeFiltersSlideOver: React.FC<Props> = ({
  isOpen,
  onClose,
  theme,
  currentFilters,
  onApplyFilters,
  properties,
}) => {
  const isLight = theme !== 'dark';
  const [filters, setFilters] = useState<ExchangeFilters>(currentFilters);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [amenityOptions, setAmenityOptions] = useState<{ id: number; name: string }[]>([]);
  // Location options come straight from the location tables, not from the
  // loaded shared properties. Two reasons: (1) shared properties are loaded
  // lazily on auth, so a stale fetch left the dropdowns empty; (2) it's
  // useful to be able to pick a department/sector even when nothing's shared
  // there yet — the empty result tells you that, instead of hiding the option.
  const [departmentOptions, setDepartmentOptions] = useState<{ id: number; name: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ id: number; name: string }[]>([]);
  const [sectorOptions, setSectorOptions] = useState<{ id: number; name: string; city_id: number }[]>([]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      setFilters(currentFilters);
    } else if (shouldRender) {
      setIsClosing(true);
      const t = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [isOpen, currentFilters, shouldRender]);

  useEffect(() => {
    if (!supabase || amenityOptions.length) return;
    supabase
      .from('amenities')
      .select('id, name')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (data) setAmenityOptions(data as any);
      });
  }, [amenityOptions.length]);

  // Property types still derive from the loaded properties (showing only
  // types that exist in the network is intentional — limits the chip cloud
  // to relevant choices). Migration get_shared_properties_expose_ids ensures
  // property_type_id is now populated.
  const propertyTypeOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of properties) {
      if (p.property_type_id && p.property_type_name) {
        map.set(Number(p.property_type_id), p.property_type_name);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'es'),
    );
  }, [properties]);

  // Load all departments once.
  useEffect(() => {
    if (!supabase || departmentOptions.length) return;
    let cancelled = false;
    supabase
      .from('departments')
      .select('id, name')
      .eq('is_active', true)
      .order('sort_order')
      .order('name')
      .then(({ data }) => {
        if (!cancelled && data) setDepartmentOptions(data as any);
      });
    return () => { cancelled = true; };
  }, [departmentOptions.length]);

  // Cascading: Sectors (cities table) for the chosen Departamento.
  useEffect(() => {
    if (!supabase || filters.departmentId == null) {
      setCityOptions([]);
      return;
    }
    let cancelled = false;
    supabase
      .from('cities')
      .select('id, name')
      .eq('department_id', filters.departmentId)
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => {
        if (!cancelled && data) setCityOptions(data as any);
      });
    return () => { cancelled = true; };
  }, [filters.departmentId]);

  // Cascading: Sub-sectors (sectors table) — union across all selected Sectors.
  // After each refetch, prune sector_ids that no longer belong to a selected
  // city so removing a city doesn't leave dangling sub-sectors filtering the
  // grid silently.
  const cityIdsKey = filters.cityIds.join(',');
  useEffect(() => {
    if (!supabase || filters.cityIds.length === 0) {
      setSectorOptions([]);
      return;
    }
    let cancelled = false;
    supabase
      .from('sectors')
      .select('id, name, city_id')
      .in('city_id', filters.cityIds)
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => {
        if (cancelled || !data) return;
        const next = data as { id: number; name: string; city_id: number }[];
        setSectorOptions(next);
        const validIds = new Set(next.map(s => s.id));
        setFilters(prev => {
          const filtered = prev.sectorIds.filter(id => validIds.has(id));
          return filtered.length === prev.sectorIds.length ? prev : { ...prev, sectorIds: filtered };
        });
      });
    return () => { cancelled = true; };
  }, [cityIdsKey]);

  if (!shouldRender) return null;

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    setFilters(defaultExchangeFilters);
    onApplyFilters(defaultExchangeFilters);
    onClose();
  };

  const togglePropertyType = (id: number) => {
    setFilters((prev) => ({
      ...prev,
      propertyTypeIds: prev.propertyTypeIds.includes(id)
        ? prev.propertyTypeIds.filter((x) => x !== id)
        : [...prev.propertyTypeIds, id],
    }));
  };

  const toggleChip = (key: 'bedrooms' | 'bathrooms' | 'parking', n: number) => {
    setFilters((prev) => {
      const current = prev[key];
      return {
        ...prev,
        [key]: current.includes(n) ? current.filter((x) => x !== n) : [...current, n].sort(),
      };
    });
  };

  const toggleAmenity = (id: number) => {
    setFilters((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(id)
        ? prev.amenityIds.filter((x) => x !== id)
        : [...prev.amenityIds, id],
    }));
  };

  const selectDepartment = (id: number | null) => {
    // Changing department resets both child arrays — cities and sectors
    // available depend entirely on it.
    setFilters((prev) => ({
      ...prev,
      departmentId: id,
      cityIds: [],
      sectorIds: [],
    }));
  };

  const selectCls = clsx(
    'w-full appearance-none outline-none cursor-pointer rounded-2xl border px-4 py-3 pr-10 text-xs font-black transition-all',
    isLight
      ? 'bg-slate-50 border-slate-200 focus:bg-white text-slate-950'
      : 'bg-white/5 border-white/5 text-white focus:bg-white/10',
  );

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex justify-end overflow-hidden">
      <div
        className={clsx(
          'absolute inset-0 bg-slate-950/60 backdrop-blur-sm',
          isClosing ? 'animate-fade-out' : 'animate-fade-in',
        )}
        onClick={onClose}
      />

      <div
        className={clsx(
          'relative w-full max-w-[440px] h-full shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col',
          isClosing ? 'animate-slide-right-out' : 'animate-slide-right',
          isLight ? 'bg-white' : 'bg-slate-900 border-l border-white/5',
        )}
      >
        {/* Header */}
        <div
          className={clsx(
            'flex items-center justify-between px-8 py-6 border-b',
            isLight ? 'border-slate-100' : 'border-white/5',
          )}
        >
          <div>
            <h3
              className={clsx(
                'font-black text-xl tracking-tight mb-1',
                isLight ? 'text-slate-900' : 'text-white',
              )}
            >
              Filtros
            </h3>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
              Propiedades Intercambio
            </p>
          </div>
          <button
            onClick={onClose}
            className={clsx(
              'p-3 rounded-2xl transition-all active:scale-90',
              isLight
                ? 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                : 'bg-white/5 text-slate-500 hover:bg-white/10 hover:text-white',
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 px-8 py-6 custom-scrollbar overflow-y-auto space-y-10">
          {/* Operation */}
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
              Tipo de Operación
            </label>
            <div
              className={clsx(
                'flex p-1.5 rounded-2xl border',
                isLight ? 'bg-slate-50 border-slate-100' : 'bg-white/5 border-white/5',
              )}
            >
              {(['All', 'sale', 'rent'] as const).map((op) => (
                <button
                  key={op}
                  onClick={() => setFilters((p) => ({ ...p, operation: op }))}
                  className={clsx(
                    'flex-1 py-2.5 text-xs font-black rounded-xl transition-all duration-300',
                    filters.operation === op
                      ? isLight
                        ? 'bg-white text-indigo-600 shadow-md shadow-slate-200 border border-slate-100'
                        : 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white',
                  )}
                >
                  {op === 'All' ? 'CUALQUIERA' : op === 'sale' ? 'VENTA' : 'ALQUILER'}
                </button>
              ))}
            </div>
          </div>

          {/* Location cascade — Departamento (single) → Sector (multi) → Sub-Sector (multi) */}
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80 flex items-center gap-2">
              <MapPin size={12} /> Ubicación
            </label>
            <div className="space-y-5">
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-400 pl-1 uppercase">Departamento</p>
                <div className="relative">
                  <select
                    value={filters.departmentId ?? ''}
                    onChange={(e) => selectDepartment(e.target.value ? Number(e.target.value) : null)}
                    className={selectCls}
                  >
                    <option value="">CUALQUIERA</option>
                    {departmentOptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name.toUpperCase()}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-baseline justify-between pl-1">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Sector</p>
                  {filters.cityIds.length > 0 && (
                    <button
                      onClick={() => setFilters((prev) => ({ ...prev, cityIds: [], sectorIds: [] }))}
                      className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <MultiSelectDropdown
                  theme={theme}
                  options={cityOptions}
                  selectedIds={filters.cityIds}
                  onChange={(ids) => setFilters((prev) => ({ ...prev, cityIds: ids }))}
                  placeholder="Selecciona uno o más sectores"
                  disabled={filters.departmentId == null}
                  disabledMessage="Selecciona un departamento primero"
                  emptyMessage="Cargando…"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-baseline justify-between pl-1">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Sub-Sector</p>
                  {filters.sectorIds.length > 0 && (
                    <button
                      onClick={() => setFilters((prev) => ({ ...prev, sectorIds: [] }))}
                      className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <MultiSelectDropdown
                  theme={theme}
                  options={sectorOptions}
                  selectedIds={filters.sectorIds}
                  onChange={(ids) => setFilters((prev) => ({ ...prev, sectorIds: ids }))}
                  placeholder="Selecciona uno o más sub-sectores"
                  disabled={filters.cityIds.length === 0}
                  disabledMessage="Selecciona uno o más sectores primero"
                  emptyMessage="Sin sub-sectores disponibles"
                />
              </div>
            </div>
          </div>

          {/* Property types */}
          {propertyTypeOptions.length > 0 && (
            <div className="space-y-4">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80 flex items-center gap-2">
                <Building2 size={12} /> Tipo de Propiedad
              </label>
              <div className="flex flex-wrap gap-2">
                {propertyTypeOptions.map((t) => {
                  const selected = filters.propertyTypeIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => togglePropertyType(t.id)}
                      className={clsx(
                        'px-4 py-2.5 rounded-xl text-[11px] font-black border transition-all duration-300',
                        selected
                          ? isLight
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                            : 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30'
                          : isLight
                            ? 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400'
                            : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20',
                      )}
                    >
                      {t.name.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price range */}
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
              Rango de Precio (USD)
            </label>
            <div className="flex gap-4">
              {(['minPrice', 'maxPrice'] as const).map((key) => (
                <div
                  key={key}
                  className={clsx(
                    'flex-1 group relative rounded-2xl border transition-all duration-300',
                    isLight
                      ? 'bg-slate-50 border-slate-200 focus-within:bg-white focus-within:ring-4 focus-within:ring-indigo-100 focus-within:border-indigo-300'
                      : 'bg-white/5 border-white/5 focus-within:bg-white/10 focus-within:border-indigo-500/50',
                  )}
                >
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 font-bold">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder={key === 'minPrice' ? 'MIN' : 'MAX'}
                    value={formatWithCommas(filters[key])}
                    onChange={(e) => {
                      const parsed = parseCommas(e.target.value);
                      if (/^\d*\.?\d*$/.test(parsed)) {
                        setFilters((prev) => ({ ...prev, [key]: parsed }));
                      }
                    }}
                    className={clsx(
                      'bg-transparent border-none py-4 px-10 outline-none w-full text-sm font-black tracking-tight placeholder:text-slate-400',
                      isLight ? 'text-slate-950' : 'text-white',
                    )}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Bedrooms */}
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
              Dormitorios
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((num) => {
                const selected = filters.bedrooms.includes(num);
                return (
                  <button
                    key={num}
                    onClick={() => toggleChip('bedrooms', num)}
                    className={clsx(
                      'flex-1 py-4 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all duration-300',
                      selected
                        ? isLight
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                          : 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30'
                        : isLight
                          ? 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400'
                          : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20',
                    )}
                  >
                    <span className="text-[17px] font-black">
                      {num}
                      {num === 5 ? '+' : ''}
                    </span>
                    <Bed size={12} className={selected ? 'text-current opacity-80' : 'opacity-30'} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bathrooms */}
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
              Baños
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((num) => {
                const selected = filters.bathrooms.includes(num);
                return (
                  <button
                    key={num}
                    onClick={() => toggleChip('bathrooms', num)}
                    className={clsx(
                      'flex-1 py-4 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all duration-300',
                      selected
                        ? isLight
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                          : 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30'
                        : isLight
                          ? 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400'
                          : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20',
                    )}
                  >
                    <span className="text-[17px] font-black">
                      {num}
                      {num === 4 ? '+' : ''}
                    </span>
                    <Bath size={12} className={selected ? 'text-current opacity-80' : 'opacity-30'} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Parking */}
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
              Parqueos
            </label>
            <div className="flex gap-2">
              {[1, 2, 3].map((num) => {
                const selected = filters.parking.includes(num);
                return (
                  <button
                    key={num}
                    onClick={() => toggleChip('parking', num)}
                    className={clsx(
                      'flex-1 py-4 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all duration-300',
                      selected
                        ? isLight
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                          : 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/30'
                        : isLight
                          ? 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-400'
                          : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20',
                    )}
                  >
                    <span className="text-[17px] font-black">
                      {num}
                      {num === 3 ? '+' : ''}
                    </span>
                    <Car size={12} className={selected ? 'text-current opacity-80' : 'opacity-30'} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Areas */}
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
                Construcción
              </label>
              <div className="space-y-2">
                {(['buildMinM2', 'buildMaxM2'] as const).map((key, idx) => (
                  <div
                    key={key}
                    className={clsx(
                      'relative rounded-2xl border px-4 py-2',
                      isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/5',
                    )}
                  >
                    <span className="text-[9px] font-bold text-slate-400 block mb-0.5">
                      {idx === 0 ? 'MÍN M²' : 'MÁX M²'}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatWithCommas(filters[key])}
                      onChange={(e) => {
                        const parsed = parseCommas(e.target.value);
                        if (/^\d*\.?\d*$/.test(parsed)) {
                          setFilters((prev) => ({ ...prev, [key]: parsed }));
                        }
                      }}
                      className={clsx(
                        'bg-transparent border-none p-0 w-full outline-none text-sm font-bold',
                        isLight ? 'text-slate-950' : 'text-white',
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
                Terreno
              </label>
              <div className="space-y-2">
                {(['landMinM2', 'landMaxM2'] as const).map((key, idx) => (
                  <div
                    key={key}
                    className={clsx(
                      'relative rounded-2xl border px-4 py-2',
                      isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/5',
                    )}
                  >
                    <span className="text-[9px] font-bold text-slate-400 block mb-0.5">
                      {idx === 0 ? 'MÍN V²' : 'MÁX V²'}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatWithCommas(filters[key])}
                      onChange={(e) => {
                        const parsed = parseCommas(e.target.value);
                        if (/^\d*\.?\d*$/.test(parsed)) {
                          setFilters((prev) => ({ ...prev, [key]: parsed }));
                        }
                      }}
                      className={clsx(
                        'bg-transparent border-none p-0 w-full outline-none text-sm font-bold',
                        isLight ? 'text-slate-950' : 'text-white',
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Amenities */}
          {amenityOptions.length > 0 && (
            <div className="space-y-4">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500/80">
                Amenidades
              </label>
              <div className="flex flex-wrap gap-2">
                {amenityOptions.map((a) => {
                  const selected = filters.amenityIds.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() => toggleAmenity(a.id)}
                      className={clsx(
                        'px-4 py-2 rounded-xl text-[11px] font-bold border transition-all duration-300',
                        selected
                          ? isLight
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100'
                            : 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/20'
                          : isLight
                            ? 'bg-white border-slate-100 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                            : 'bg-white/5 border-white/5 text-slate-400 hover:bg-white/10 hover:border-white/20',
                      )}
                    >
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={clsx(
            'p-8 border-t flex items-center justify-between gap-4',
            isLight ? 'bg-slate-50/80 border-slate-100' : 'bg-slate-900/80 border-white/5',
          )}
        >
          <button
            onClick={handleReset}
            className="text-xs font-black text-slate-500 hover:text-rose-500 uppercase tracking-widest transition-colors flex items-center gap-2"
          >
            <X size={14} /> Limpiar
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-4 px-6 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-black tracking-widest uppercase shadow-[0_10px_30px_rgba(79,70,229,0.4)] transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            Aplicar Filtros
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
